import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { CalendarDays, MessagesSquare, Users, UserCog } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useIsDesktop } from '../../lib/useIsDesktop';
import { hapticSelection } from '../../lib/haptics';
import Events from './Events';
import Communaute, { AnnuaireTab } from './Communaute';

// Onglets du hub. `desktopHidden` = déjà accessible via la barre latérale
// desktop (ici « Événements ») → masqué sur grand écran pour éviter le doublon.
const TABS = [
  { id: 'events', label: 'Événements', icon: CalendarDays, desktopHidden: true },
  { id: 'forum', label: 'Forum', icon: MessagesSquare },
  { id: 'annuaire', label: 'Annuaire', icon: Users },
];

/**
 * Hub Communauté — la vie associative en onglets :
 *  1. Événements (liste, détails, rapports)
 *  2. Forum (mur, galerie, intentions de prière, reconnaissance)
 *  3. Annuaire des membres — consultable par tous. La gestion des membres
 *     (Members.jsx) est réservée aux admins/responsables, accessible via le
 *     bouton « Gérer » et la route /dashboard/members.
 */
export default function CommunauteHub() {
  const { isAdmin } = useAuth();
  // La gestion des membres (Members.jsx) est réservée aux administrateurs.
  const canManage = isAdmin;
  const isDesktop = useIsDesktop();

  // Sur desktop, on retire « Événements » (présent dans la barre latérale).
  const tabs = TABS.filter((t) => !(isDesktop && t.desktopHidden));
  const tabIds = tabs.map((t) => t.id);

  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab');
  const [tab, setTab] = useState(requested || 'events');
  // Onglet effectif : ignore un onglet masqué (ex. « events » sur desktop).
  const activeTab = tabIds.includes(tab) ? tab : tabIds[0];

  const onChange = (value) => {
    hapticSelection();
    setTab(value);
    setSearchParams({ tab: value }, { replace: true });
  };

  return (
    <Tabs value={activeTab} onValueChange={onChange} className="space-y-4">
      <TabsList className={`grid w-full ${tabs.length === 2 ? 'grid-cols-2' : 'grid-cols-3'} h-auto p-1 rounded-2xl bg-muted shadow-sm ring-1 ring-border/50 md:sticky md:top-0 z-30 backdrop-blur`}>
        {tabs.map((t) => (
          <TabsTrigger key={t.id} value={t.id} className="flex-col gap-1 py-2 rounded-xl text-xs data-[state=active]:shadow">
            <t.icon className="h-4 w-4" />
            {t.label}
          </TabsTrigger>
        ))}
      </TabsList>

      <TabsContent value="events" className="mt-0 focus-visible:outline-none">
        <Events />
      </TabsContent>

      <TabsContent value="forum" className="mt-0 focus-visible:outline-none">
        <Communaute />
      </TabsContent>

      <TabsContent value="annuaire" className="mt-0 focus-visible:outline-none">
        <div className="space-y-4">
          {/* En-tête de l'annuaire (compact en mobile) */}
          <div className="rounded-2xl md:rounded-3xl bg-gradient-to-br from-sky-600 to-indigo-700 p-4 md:p-6 text-white shadow-lg">
            <div className="flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                <div className="rounded-xl md:rounded-2xl bg-white/20 p-2.5 md:p-3 shrink-0">
                  <Users className="h-6 w-6 md:h-7 md:w-7" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-xl md:text-2xl font-bold">Annuaire</h1>
                  <p className="text-xs md:text-sm text-white/80 hidden sm:block">
                    Retrouvez et contactez les membres de la Daara.
                  </p>
                </div>
              </div>
              {canManage && (
                <Button asChild size="sm" className="shrink-0 gap-1.5 border-0 bg-white/15 text-white hover:bg-white/25">
                  <Link to="/dashboard/members">
                    <UserCog className="h-4 w-4" />
                    <span className="hidden sm:inline">Gérer</span>
                  </Link>
                </Button>
              )}
            </div>
          </div>

          <AnnuaireTab />
        </div>
      </TabsContent>
    </Tabs>
  );
}
